import React, { useState, useEffect } from "react";
import { Card } from "views/components/ui/Card";
import { Button } from "views/components/ui/Button";
import { Play, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const QueueManagement = () => {
    const [queueStatus, setQueueStatus] = useState({
        queueLength: 0,
        isProcessing: false,
        pendingTools: [],
    });
    const [isLoading, setIsLoading] = useState(false);

    const fetchQueueStatus = async () => {
        try {
            const response = await axios.get("/api/tool/queue/status");
            setQueueStatus(response.data);
        } catch (error) {
            console.error("Error fetching queue status:", error);
            toast.error("Failed to fetch queue status");
        }
    };

    const handleProcessQueue = async () => {
        setIsLoading(true);
        try {
            const response = await axios.post("/api/tool/queue/process");
            toast.success(response.data.message);
            // Refresh queue status after a short delay
            setTimeout(fetchQueueStatus, 1000);
        } catch (error) {
            console.error("Error processing queue:", error);
            toast.error(
                error.response?.data?.error || "Failed to process queue"
            );
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch queue status periodically
    useEffect(() => {
        fetchQueueStatus();
        const interval = setInterval(fetchQueueStatus, 5000); // Refresh every 5 seconds
        return () => clearInterval(interval);
    }, []);

    return (
        <Card>
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-semibold">
                        Tool Processing Queue
                    </h2>
                    <div className="flex gap-2">
                        <Button
                            onClick={handleProcessQueue}
                            disabled={
                                isLoading ||
                                queueStatus.isProcessing ||
                                queueStatus.queueLength === 0
                            }
                            variant="primary"
                            size="sm"
                        >
                            <Play className="w-4 h-4 mr-2" />
                            {isLoading ? "Processing..." : "Process Queue"}
                        </Button>
                        <Button
                            onClick={fetchQueueStatus}
                            variant="outline"
                            size="sm"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Refresh
                        </Button>
                    </div>
                </div>

                <div className="mb-4">
                    <div className="grid grid-cols-3 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="text-sm text-gray-600">
                                Queue Length
                            </div>
                            <div className="text-2xl font-semibold">
                                {queueStatus.queueLength}
                            </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="text-sm text-gray-600">Status</div>
                            <div className="text-2xl font-semibold">
                                {queueStatus.isProcessing
                                    ? "Processing"
                                    : "Idle"}
                            </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="text-sm text-gray-600">
                                Next Tool
                            </div>
                            <div className="text-lg font-semibold truncate">
                                {queueStatus.pendingTools[0]?.name || "None"}
                            </div>
                        </div>
                    </div>
                </div>

                {queueStatus.pendingTools.length > 0 && (
                    <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-2">
                            Pending Tools
                        </h3>
                        <div className="bg-white border rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {queueStatus.pendingTools.map((tool) => (
                                        <tr key={tool.uuid}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {tool.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {tool.status}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
};

export default QueueManagement;
